import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AccountService } from '../../../../utils/ServiceLocator';
import { getToken } from '../../../../utils/AdalConfig';

export type ReimbursementCalcPhase = 'idle' | 'calculating' | 'complete';
export type ReimbursementRowStatus = 'pending' | 'calculating' | 'success' | 'warning' | 'error';

export interface ReimbursementCalcRow {
    accountId: string;
    accountNumber: string;
    status: ReimbursementRowStatus;
    errorMessage?: string;
    totalReimbursement?: number;
}

export interface BatchReimbursementResponse {
    accountId: string;
    success: boolean;
    errorMessage?: string;
    totalCalculatedReimbursement: number;
    procedures: {
        procedureCode: string;
        billedCharges: number;
        calculatedReimbursement: number;
    }[];
}

interface ReimbursementCalcState {
    rows: ReimbursementCalcRow[];
    phase: ReimbursementCalcPhase;
    processedCount: number;
    successCount: number;
    warningCount: number;
    errorCount: number;
}

const initialState: ReimbursementCalcState = {
    rows: [],
    phase: 'idle',
    processedCount: 0,
    successCount: 0,
    warningCount: 0,
    errorCount: 0
};

// Async thunk to calculate reimbursement for multiple accounts in batch
export interface CalculateBatchReimbursementRequest {
    accountIds: string[];
}

export const calculateBatchReimbursement = createAsyncThunk<
    BatchReimbursementResponse[],
    CalculateBatchReimbursementRequest,
    { rejectValue: string }
>(
    'reimbursementCalc/calculateBatch',
    async (request, { rejectWithValue }) => {
        try {
            const startTime = performance.now();
            const token = getToken();
            const response = await fetch(
                `${AccountService}CalculateReimbursementBatch`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        accountIds: request.accountIds
                    })
                }
            );
            const responseTime = performance.now() - startTime;

            if (!response.ok) {
                const errorText = await response.text();
                console.log(`Batch API call failed after ${responseTime.toFixed(2)}ms`);
                return rejectWithValue(errorText || 'Batch calculation request failed');
            }

            const results: BatchReimbursementResponse[] = await response.json();
            console.log(`Batch API call for ${request.accountIds.length} accounts completed in ${responseTime.toFixed(2)}ms`);
            return results;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Network error during batch calculation');
        }
    }
);

export const ReimbursementCalcSlice = createSlice({
    name: 'reimbursementCalc',
    initialState,
    reducers: {
        loadAccounts: (state, action: PayloadAction<{ accounts: { accountId: string; accountNumber: string }[] }>) => {
            state.rows = action.payload.accounts.map(acc => ({
                accountId: acc.accountId,
                accountNumber: acc.accountNumber,
                status: 'pending' as ReimbursementRowStatus,
                errorMessage: undefined,
                totalReimbursement: undefined
            }));
            state.phase = 'calculating';
            state.processedCount = 0;
            state.successCount = 0;
            state.warningCount = 0;
            state.errorCount = 0;
        },
        setPhase: (state, action: PayloadAction<ReimbursementCalcPhase>) => {
            state.phase = action.payload;
        },
        resetCalculation: () => initialState
    },
    extraReducers: (builder) => {
        builder
            .addCase(calculateBatchReimbursement.pending, (state) => {
                // Mark all pending rows as calculating
                state.rows.forEach(row => {
                    if (row.status === 'pending') {
                        row.status = 'calculating';
                    }
                });
            })
            .addCase(calculateBatchReimbursement.fulfilled, (state, action) => {
                // Process each response from the batch
                action.payload.forEach(response => {
                    const row = state.rows.find(r => r.accountId === response.accountId);
                    if (row) {
                        if (response.success) {
                            if (response.errorMessage) {
                                // Success with message = warning
                                row.status = 'warning';
                                row.errorMessage = response.errorMessage;
                                state.warningCount++;
                            } else {
                                row.status = 'success';
                            }
                            row.totalReimbursement = response.totalCalculatedReimbursement;
                            state.successCount++;
                        } else {
                            row.status = 'error';
                            row.errorMessage = response.errorMessage;
                            state.errorCount++;
                        }
                        state.processedCount++;
                    }
                });
            })
            .addCase(calculateBatchReimbursement.rejected, (state, action) => {
                // Mark all calculating rows as error
                state.rows.forEach(row => {
                    if (row.status === 'calculating') {
                        row.status = 'error';
                        row.errorMessage = action.payload || 'Batch calculation failed';
                        state.errorCount++;
                        state.processedCount++;
                    }
                });
            });
    }
});

export const { 
    loadAccounts, 
    setPhase, 
    resetCalculation
} = ReimbursementCalcSlice.actions;

export default ReimbursementCalcSlice.reducer;
